import { api } from "../lib/api.js";

interface ChatPlatformState {
  platform: "twitch" | "kick";
  label: string;
  available: boolean;
  connected: boolean;
  login: string | null;
  enabled: boolean;
  last_posted_at: string | null;
  last_error: string | null;
}

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

/**
 * Links in chat: connect Twitch or Kick once, and the promotion on screen is
 * posted into the streamer's own chat with a clickable link. The sign-in is on
 * the platform's own page, in a new tab; this box looks again when the tab
 * comes back into focus.
 */
export async function renderChatLinks(box: HTMLElement): Promise<void> {
  const load = async () => {
    let platforms: ChatPlatformState[];
    try {
      platforms = (await api<{ platforms: ChatPlatformState[] }>("GET", "/v1/chat/connections")).platforms;
    } catch (err) {
      box.innerHTML = `<p class="error">${esc(err instanceof Error ? err.message : String(err))}</p>`;
      return;
    }
    box.classList.remove("loading");
    box.innerHTML = platforms
      .map((p) => {
        const status = !p.available
          ? "Coming soon"
          : !p.connected
            ? "Not connected"
            : p.enabled
              ? `Posting as <strong>${esc(p.login ?? "")}</strong>`
              : "Paused";
        const buttons = !p.available
          ? ""
          : !p.connected
            ? `<button class="btn" data-act="connect" data-p="${p.platform}">Connect ${esc(p.label)}</button>`
            : `<button class="btn" data-act="toggle" data-p="${p.platform}" data-on="${p.enabled ? "0" : "1"}">${p.enabled ? "Pause" : "Resume"}</button>
               <button class="btn btn-ghost" data-act="disconnect" data-p="${p.platform}" data-label="${esc(p.label)}">Disconnect</button>`;
        return `<div class="chat-row">
          <p><strong>${esc(p.label)}</strong> · <span class="meta">${status}</span></p>
          ${p.last_error ? `<p class="meta warn">${esc(p.last_error)}</p>` : ""}
          <div class="actions">${buttons}</div>
        </div>`;
      })
      .join("");
  };

  box.addEventListener("click", async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-act]");
    if (!b) return;
    const p = b.dataset.p!;
    b.disabled = true;
    try {
      if (b.dataset.act === "connect") {
        const r = await api<{ url: string }>("POST", `/v1/chat/${p}/connect`);
        window.open(r.url, "_blank", "noopener");
      } else if (b.dataset.act === "toggle") {
        await api("PUT", `/v1/chat/${p}`, { enabled: b.dataset.on === "1" });
      } else if (b.dataset.act === "disconnect") {
        if (!confirm(`Disconnect ${b.dataset.label}? Product links stop being posted in that chat.`)) return;
        await api("DELETE", `/v1/chat/${p}`);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      b.disabled = false;
      await load();
    }
  });
  window.addEventListener("focus", () => void load());
  await load();
}
